/**
 * Las lecciones. Datos, no código.
 *
 * Ordered the way the work is: the till before the sale, the sale before the corte, goods
 * before prices before selling them. Somebody who starts at the top and keeps going is taken
 * through a whole day and then a whole month, and never meets a screen that assumes something
 * they have not been shown.
 *
 * Every lesson declares which permissions it needs, and the ones a person cannot use are not
 * listed for them at all. A cashier is never taught payroll — not because it is hidden from
 * her, but because teaching somebody a door they cannot open is how a tutorial teaches that
 * the app is against them.
 *
 * `cuando` compares now against a snapshot taken when the lesson started, so a step means "you
 * did this, just now" rather than "the shop happens to be in this state". Practising the same
 * lesson twice works; so does doing the task before the tutorial gets to that step.
 */

import type { Permission } from '@abastece/shared';
import type { NombreIcono, Tinte } from '../ui/Iconos.js';
import type { Instantanea } from './motor.js';

/** The routes a lesson can send somebody to. Narrow so `navigate` keeps typechecking. */
export type Ruta =
  | '/'
  | '/mas'
  | '/pos'
  | '/caja'
  | '/caja/empezar'
  | '/caja/terminar'
  | '/ventas'
  | '/productos'
  | '/productos/nuevo'
  | '/precios'
  | '/compras/nueva'
  | '/inventario'
  | '/inventario/conteo'
  | '/clientes'
  | '/clientes/pedidos/nuevo'
  | '/pedidos'
  | '/gastos'
  | '/contabilidad'
  | '/planilla'
  | '/ajustes';

export interface Paso {
  /** Suffix of the i18n key: `tutorial.<leccion>.<id>`. */
  id: string;
  /** Go here before the step runs. Skipped if already there. */
  ruta?: Ruta;
  /** `data-guia` value of the element to spotlight. Absent means a card in the middle. */
  ancla?: string;
  /** Done when the shop changed. The strongest kind, and the default choice. */
  cuando?: (ahora: Instantanea, inicio: Instantanea) => boolean;
  /** Done when this selector matches something on screen. For mid-task, pre-commit states. */
  apareceEl?: string;
  /** Done when the spotlighted thing is tapped. Only where tapping IS the task. */
  alTocar?: boolean;
  /**
   * The spotlight is something to type on — a PIN pad. On a short screen the card shrinks
   * beside it and scrolls inside itself rather than lying across it: a covered key is a step
   * nobody can finish.
   */
  noTapar?: boolean;
  /**
   * The step hands the screen to a wizard («Empezar el día», «Terminar mi turno»), which says on
   * each of its screens what to do: nothing is dimmed, nothing pointed at, and the card is a strip
   * across the top that the wizard leaves room for. With `cuando` or `apareceEl`, for what ends it.
   */
  suelta?: boolean;
  /**
   * Shown only when this holds as the lesson starts, and passed over unseen when it does not:
   * the steps that put the shop the way the lesson needs it, for somebody who came to it out of
   * order — «Hacer una venta» with the till still closed. Only the steps a lesson starts with
   * (`__tests__/lecciones.test.ts`), so the count of steps is known from the start.
   */
  soloSi?: (ahora: Instantanea) => boolean;
  /**
   * Shown only to somebody with one of these, and passed over, uncounted, for anybody else: a step
   * about something their screens do not show them, such as the price a cost suggests, which a
   * cashier who keeps the prices does not see (2026-10-09).
   */
  permisos?: readonly Permission[];
}

export interface Leccion {
  id: string;
  icono: NombreIcono;
  tinte: Tinte;
  /** Any one of these is enough. Empty means everybody. */
  permisos: readonly Permission[];
  pasos: readonly Paso[];
}

const cerrada = (a: Instantanea) => !a.cajaAbierta;

/**
 * A lesson that sells, voids or counts the till needs it open, and the practice shop starts the
 * day with it closed: somebody who skipped «Empezar el día» opens it here, as that lesson does.
 * Opening is a wizard that says on each of its screens what to do (the owner, 2026-10-03), so the
 * lesson says where it is, then hands it the screen (`suelta`) until the wizard's last screen:
 * moving on as soon as the till was open took the lesson to its next screen before what the count
 * found against the last shift had been read. The till's own screen, open, ends it too, for
 * somebody who left the wizard before its end.
 */
const abrirPrimero = [
  { id: 'cajaCerrada', ruta: '/caja/empezar', ancla: 'dia-empezar', soloSi: cerrada },
  {
    id: 'abrirCaja',
    ruta: '/caja/empezar',
    soloSi: cerrada,
    suelta: true,
    apareceEl: '[data-guia="dia-abierta"], [data-guia="caja-corte"]',
  },
] as const;

const CRUDAS = [
  // -------------------------------------------------------------------------
  {
    id: 'rumbo',
    icono: 'negocio',
    tinte: 'marca',
    permisos: [],
    pasos: [
      { id: 'bienvenida' },
      { id: 'practica' },
      { id: 'inicio', ruta: '/', ancla: 'inicio-principal' },
      { id: 'atajos', ruta: '/', ancla: 'inicio-atajos' },
      { id: 'pendientes', ruta: '/', ancla: 'inicio-avisos' },
      { id: 'mas', ruta: '/', ancla: 'inicio-mas', alTocar: true },
      { id: 'grupos', ruta: '/mas', ancla: 'mas-grupos' },
      // «Ventas, caja y recargas» is the one section every role has.
      { id: 'pestanas', ruta: '/ventas', ancla: 'pestanas' },
      // From inside a section, where «Volver» going back to the menu instead of home shows.
      { id: 'volver', ruta: '/ventas', ancla: 'pagina-volver', alTocar: true },
      { id: 'cierre' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'caja',
    icono: 'caja',
    tinte: 'esmeralda',
    permisos: ['abrir_caja'],
    pasos: [
      // Repeated after the till was opened: there is nothing to open until a corte closes it.
      {
        id: 'yaAbierta',
        ruta: '/caja',
        soloSi: (a) => a.cajaAbierta,
        cuando: (a) => !a.cajaAbierta,
      },
      { id: 'porque' },
      { id: 'ir', ruta: '/caja', ancla: 'caja-empezar', alTocar: true },
      // What the wizard is, on its first screen; then the screen is the wizard's, a step at a time,
      // until the till is open.
      { id: 'pasos', ruta: '/caja/empezar', ancla: 'dia-empezar' },
      { id: 'contar', ruta: '/caja/empezar', suelta: true, cuando: (a) => a.cajaAbierta },
      // The count against what the last shift left, the wizard's first screen once the till is open.
      { id: 'listo', ruta: '/caja/empezar', ancla: 'caja-abrir' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'vender',
    icono: 'vender',
    tinte: 'marca',
    permisos: ['vender'],
    pasos: [
      ...abrirPrimero,
      { id: 'porque' },
      { id: 'buscar', ruta: '/pos', ancla: 'pos-buscar', apareceEl: '[data-guia="pos-linea"]' },
      { id: 'linea', ruta: '/pos', ancla: 'pos-linea' },
      // Done once a line has more than one, whether it was + or typing the number that got it there.
      { id: 'cantidad', ruta: '/pos', ancla: 'pos-cantidad', apareceEl: '[data-guia="pos-linea"][data-varios]' },
      { id: 'total', ruta: '/pos', ancla: 'pos-total' },
      {
        id: 'cobrar',
        ruta: '/pos',
        ancla: 'pos-cobrar',
        apareceEl: '[data-guia="cobro-metodos"]',
      },
      { id: 'metodo', ancla: 'cobro-metodos' },
      { id: 'monto', ancla: 'cobro-monto' },
      {
        id: 'confirmar',
        ancla: 'cobro-confirmar',
        cuando: (a, i) => a.ventas > i.ventas,
      },
      { id: 'recibo' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'vuelto',
    icono: 'ventas',
    tinte: 'marca',
    permisos: ['vender'],
    pasos: [
      ...abrirPrimero,
      { id: 'porque' },
      { id: 'arma', ruta: '/pos', ancla: 'pos-buscar', apareceEl: '[data-guia="pos-linea"]' },
      { id: 'cobrar', ruta: '/pos', ancla: 'pos-cobrar', apareceEl: '[data-guia="cobro-monto"]' },
      // The change shows while it is being typed, so seeing it is the whole step.
      { id: 'masDeLaCuenta', ancla: 'cobro-monto', apareceEl: '[data-guia="cobro-vuelto"]' },
      { id: 'vuelto', ancla: 'cobro-vuelto' },
      // Shown while the panel is still open, so there is a button to point at. Pressing it
      // here only selects the amount; the sale can still be confirmed as it stands.
      { id: 'partido', ancla: 'cobro-dividir' },
      { id: 'confirmar', ancla: 'cobro-confirmar', cuando: (a, i) => a.ventas > i.ventas },
    ],
  },

  // -------------------------------------------------------------------------
  // Somebody who owes comes in, pays some of it, and takes more on fiado: the whole of fiado at
  // the counter in one visit, which is how it happens. The abono first, because the client is
  // already on the sale for it; a charged sale starts the next one with nobody on it.
  {
    id: 'fiado',
    icono: 'fiado',
    tinte: 'violeta',
    permisos: ['vender'],
    pasos: [
      ...abrirPrimero,
      { id: 'porque' },
      { id: 'cliente', ruta: '/pos', ancla: 'pos-cliente', apareceEl: '[data-guia="pos-clientes"]' },
      // Somebody on the sale once the picker has closed: with a client already on it (a sale left
      // half-rung), the row alone would pass this step while the picker was still open.
      {
        id: 'elegir',
        ancla: 'pos-clientes',
        apareceEl: 'body:not(:has([data-guia="pos-clientes"])) [data-guia="pos-cliente"][data-con-cliente]',
      },
      { id: 'abonar', ruta: '/pos', ancla: 'pos-abonar', apareceEl: '[data-guia="abono-form"]' },
      { id: 'abono', ancla: 'abono-form', cuando: (a, i) => a.abonos > i.abonos },
      { id: 'comprobante', ancla: 'abono-listo', alTocar: true },
      { id: 'agregar', ruta: '/pos', ancla: 'pos-buscar', apareceEl: '[data-guia="pos-linea"]' },
      { id: 'cobrar', ruta: '/pos', ancla: 'pos-cobrar', apareceEl: '[data-guia="cobro-metodos"]' },
      { id: 'fiar', ancla: 'cobro-metodos', apareceEl: '[data-guia="cobro-fiado"]' },
      { id: 'cuenta', ancla: 'cobro-fiado' },
      { id: 'confirmar', ancla: 'cobro-confirmar', cuando: (a, i) => a.ventasFiadas > i.ventasFiadas },
      { id: 'queda' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'corregir',
    icono: 'bitacora',
    tinte: 'rosa',
    permisos: ['anular_venta'],
    pasos: [
      // A sale's cash goes back out of an open till.
      ...abrirPrimero,
      { id: 'porque' },
      { id: 'ir', ruta: '/ventas', ancla: 'ventas-lista' },
      { id: 'abrir', ruta: '/ventas', ancla: 'ventas-lista', apareceEl: '[data-guia="venta-anular"]' },
      { id: 'anular', ancla: 'venta-anular', apareceEl: '[data-guia="venta-motivo"]' },
      { id: 'motivo', ancla: 'venta-motivo' },
      { id: 'stock' },
      { id: 'confirmar', ancla: 'venta-confirmar-anular', apareceEl: '[data-guia="pin-teclado"]' },
      // A void asks for the PIN of whoever is signed in; the step says the practice shop's.
      {
        id: 'pin',
        ancla: 'pin-teclado',
        noTapar: true,
        cuando: (a, i) => a.ventasAnuladas > i.ventasAnuladas,
      },
      { id: 'queda' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'cerrarCaja',
    icono: 'caja',
    tinte: 'esmeralda',
    permisos: ['cerrar_caja'],
    pasos: [
      ...abrirPrimero,
      { id: 'porque' },
      { id: 'ir', ruta: '/caja', ancla: 'caja-corte', alTocar: true },
      // «Terminar mi turno» asks first whether anybody comes after: the lesson is the day's end. Its
      // answers are tapped, and a spotlight on the question and both answers lay under the card on a
      // small phone, so the screen is the wizard's from here.
      { id: 'quien', ruta: '/caja/terminar', suelta: true, apareceEl: '[data-guia="turno-cordobas"]' },
      // The count, note by note, and the dollars: the wizard's to say, a screen at a time. A list
      // of every note is taller than a phone, and a spotlight on it put the card across it.
      { id: 'aCiegas', ruta: '/caja/terminar', suelta: true },
      { id: 'contar', ruta: '/caja/terminar', suelta: true, apareceEl: '[data-guia="turno-queda"]' },
      { id: 'queda', ruta: '/caja/terminar', ancla: 'turno-queda' },
      {
        id: 'cerrar',
        ruta: '/caja/terminar',
        suelta: true,
        cuando: (a, i) => a.cortes > i.cortes,
      },
      { id: 'diferencia', ruta: '/caja/terminar', ancla: 'caja-diferencia' },
      { id: 'sacar', ruta: '/caja/terminar', ancla: 'caja-saque' },
      // The day's summary, what is left for the owner, tomorrow's orders and the server.
      { id: 'dia', ruta: '/caja/terminar', suelta: true, apareceEl: '[data-guia="turno-listo"]' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'productos',
    icono: 'productos',
    tinte: 'cielo',
    permisos: ['editar_productos'],
    pasos: [
      { id: 'porque' },
      { id: 'ir', ruta: '/productos', ancla: 'productos-nuevo', alTocar: true },
      { id: 'nombre', ruta: '/productos/nuevo', ancla: 'producto-nombre' },
      { id: 'unidad', ancla: 'producto-unidad' },
      { id: 'categoria', ancla: 'producto-categoria' },
      {
        id: 'guardar',
        ancla: 'producto-guardar',
        cuando: (a, i) => a.productos > i.productos,
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'precios',
    icono: 'precios',
    tinte: 'cielo',
    permisos: ['editar_precios'],
    pasos: [
      { id: 'porque' },
      // «Sin precio» first, as the screen opens; in a shop where everything has a price it is
      // empty, so every product, and a price changed rather than a new one.
      {
        id: 'todos',
        ruta: '/precios',
        ancla: 'precios-filtro',
        apareceEl: '[data-guia="precios-filtro"][data-todos]',
      },
      { id: 'ir', ruta: '/precios', ancla: 'precios-lista' },
      {
        id: 'escribir',
        ruta: '/precios',
        ancla: 'precios-lista',
        cuando: (a, i) => a.sumaPrecios !== i.sumaPrecios,
      },
      { id: 'sugerido', permisos: ['ver_costos'] },
      // «Sin costo» and the row's «Costo», since 2026-10-18: what makes a sale look all profit.
      { id: 'costos', permisos: ['ver_costos'] },
      { id: 'listas' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'comprar',
    icono: 'recibir',
    tinte: 'cielo',
    permisos: ['recibir_compras'],
    pasos: [
      { id: 'porque' },
      // In the screen's order: what came in first, then the invoice it came with.
      { id: 'agregar', ruta: '/compras/nueva', ancla: 'compra-agregar', apareceEl: '[data-guia="compra-linea"]' },
      { id: 'costo', ancla: 'compra-linea' },
      { id: 'proveedor', ancla: 'compra-proveedor' },
      { id: 'pago', ancla: 'compra-pago' },
      { id: 'flete', ancla: 'compra-flete' },
      {
        id: 'registrar',
        ancla: 'compra-registrar',
        cuando: (a, i) => a.compras > i.compras,
      },
      { id: 'costoPromedio', permisos: ['ver_costos'] },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'inventario',
    icono: 'inventario',
    tinte: 'cielo',
    permisos: ['ajustar_inventario'],
    pasos: [
      { id: 'porque' },
      { id: 'ver', ruta: '/inventario', ancla: 'inventario-lista' },
      {
        id: 'contar',
        ruta: '/inventario/conteo',
        ancla: 'conteo-agregar',
        apareceEl: '[data-guia="conteo-filas"]',
      },
      // Over the product just added, where what was found is typed.
      { id: 'aCiegas', ancla: 'conteo-filas' },
      {
        id: 'registrar',
        ancla: 'conteo-registrar',
        cuando: (a, i) => a.conteos > i.conteos,
      },
      { id: 'merma' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'clientes',
    icono: 'clientes',
    tinte: 'violeta',
    permisos: ['gestionar_clientes'],
    pasos: [
      { id: 'porque' },
      { id: 'ir', ruta: '/clientes', ancla: 'clientes-nuevo', alTocar: true },
      { id: 'nombre', ancla: 'cliente-nombre' },
      { id: 'tipo', ancla: 'cliente-tipo' },
      { id: 'lista', ancla: 'cliente-lista' },
      { id: 'dia', ancla: 'cliente-dia' },
      {
        id: 'guardar',
        ancla: 'cliente-guardar',
        cuando: (a, i) => a.clientes > i.clientes,
      },
      { id: 'enLaVenta' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'pedidos',
    icono: 'pedidos',
    tinte: 'violeta',
    permisos: ['gestionar_clientes'],
    pasos: [
      { id: 'porque' },
      // The list first, as every order is found later; the order itself on a screen of its own.
      { id: 'ir', ruta: '/pedidos', ancla: 'pedidos-nuevo', alTocar: true },
      { id: 'cliente', ruta: '/clientes/pedidos/nuevo', ancla: 'pedido-cliente' },
      { id: 'entrega', ancla: 'pedido-entrega' },
      { id: 'agregar', ancla: 'pedido-agregar', apareceEl: '[data-guia="pedido-linea"]' },
      {
        id: 'guardar',
        ancla: 'pedido-guardar',
        cuando: (a, i) => a.pedidos > i.pedidos,
      },
      // Back on the list: how an order is delivered, said over «Cobrar» rather than done, since the
      // till may be closed, and charging is «Hacer una venta»'s.
      { id: 'cobrar', ruta: '/pedidos', ancla: 'pedido-cobrar' },
      { id: 'noReserva' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'gastos',
    icono: 'gastos',
    tinte: 'ambar',
    permisos: ['ver_reportes'],
    pasos: [
      { id: 'porque' },
      { id: 'ir', ruta: '/gastos', ancla: 'gastos-nuevo', alTocar: true },
      { id: 'categoria', ancla: 'gasto-categoria' },
      { id: 'descripcion', ancla: 'gasto-descripcion' },
      { id: 'monto', ancla: 'gasto-monto' },
      {
        id: 'guardar',
        ancla: 'gasto-guardar',
        cuando: (a, i) => a.gastos > i.gastos,
      },
      // Each where it is on the screen: the owner's money and what is owed have a tab each.
      { id: 'noEsRetiro', ancla: 'gastos-ver-dueno' },
      { id: 'verPorPagar', ruta: '/gastos', ancla: 'gastos-ver-por-pagar', alTocar: true },
      { id: 'proveedores', ancla: 'gastos-por-pagar' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'numeros',
    icono: 'ganancia',
    tinte: 'esmeralda',
    permisos: ['ver_reportes'],
    pasos: [
      { id: 'porque' },
      { id: 'ir', ruta: '/contabilidad', ancla: 'conta-periodo' },
      { id: 'ventas', ruta: '/contabilidad', ancla: 'conta-ventas' },
      { id: 'utilidad', ruta: '/contabilidad', ancla: 'conta-utilidad' },
      { id: 'margen' },
      { id: 'gastos', ruta: '/contabilidad', ancla: 'conta-gastos' },
      { id: 'cuandoMirar' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'planilla',
    icono: 'personal',
    tinte: 'ambar',
    permisos: ['gestionar_planilla'],
    pasos: [
      { id: 'porque' },
      { id: 'ir', ruta: '/planilla', ancla: 'planilla-gente' },
      { id: 'acumulado', ruta: '/planilla', ancla: 'planilla-acumulado' },
      { id: 'correr', ruta: '/planilla', ancla: 'planilla-correr', alTocar: true },
      { id: 'unaVez' },
      { id: 'pagar' },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'respaldo',
    icono: 'ajustes',
    tinte: 'neutro',
    permisos: [],
    pasos: [
      { id: 'porque' },
      { id: 'sync', ruta: '/ajustes', ancla: 'ajustes-sync' },
      { id: 'sinConexion' },
      { id: 'codigo', ruta: '/ajustes', ancla: 'ajustes-dispositivo' },
      { id: 'idioma', ruta: '/ajustes', ancla: 'ajustes-idioma' },
      { id: 'final' },
    ],
  },
] as const satisfies readonly Leccion[];

export const LECCIONES: readonly Leccion[] = CRUDAS;

/**
 * Los nombres de lección y paso, como literales.
 *
 * `as const` above is what keeps every id a literal instead of widening to `string`, and that
 * is what lets `i18n/completo.ts` prove at compile time that every lesson and every step has
 * text in both catalogues. Delete a string and the build fails; add a step and the build fails
 * until it has one. That proof is what the two casts in `Tutorial.tsx` stand on.
 */
export type IdLeccion = (typeof CRUDAS)[number]['id'];

type PasosDe = {
  [L in (typeof CRUDAS)[number] as L['id']]: L['pasos'][number]['id'];
};

/** Exactly the valid `tutorial.<leccion>.<paso>` keys — not the cross product. */
export type ClavePaso = {
  [K in keyof PasosDe]: `tutorial.${K & string}.${PasosDe[K] & string}`;
}[keyof PasosDe];

export type ClaveLeccion = `tutorial.${IdLeccion}.titulo` | `tutorial.${IdLeccion}.para`;

/** How many steps somebody is sure to see: all but one shown only when the shop needs it. */
export const pasosQueSeVen = (l: Leccion): number => l.pasos.filter((p) => !p.soloSi).length;

/** Where a lesson starts: past the steps it starts with that would find the shop already right. */
export function pasoInicial(l: Leccion, ahora: Instantanea): number {
  let i = 0;
  while (l.pasos[i]?.soloSi && !l.pasos[i]!.soloSi!(ahora)) i++;
  return i;
}

/** The lessons somebody with these permissions should be offered, in order. */
export function leccionesPara(puede: (p: Permission) => boolean): Leccion[] {
  return LECCIONES.filter((l) => l.permisos.length === 0 || l.permisos.some(puede));
}

/** A lesson as somebody with these permissions takes it: without the steps that are not theirs. */
export function pasosPara(l: Leccion, puede: (p: Permission) => boolean): Leccion {
  if (l.pasos.every((p) => !p.permisos)) return l;
  return { ...l, pasos: l.pasos.filter((p) => !p.permisos || p.permisos.some(puede)) };
}

export const leccionPorId = (id: string): Leccion | undefined =>
  LECCIONES.find((l) => l.id === id);

/**
 * Keys built from lesson data.
 *
 * The cast is safe because `i18n/completo.ts` asserts `ClaveLeccion` and `ClavePaso` are both
 * subsets of `Clave`. Building the string here rather than at each call site means there is
 * exactly one place where that reasoning has to hold.
 */
export const claveTitulo = (leccion: string) => `tutorial.${leccion}.titulo` as ClaveLeccion;
export const clavePara = (leccion: string) => `tutorial.${leccion}.para` as ClaveLeccion;
export const clavePaso = (leccion: string, paso: string) =>
  `tutorial.${leccion}.${paso}` as ClavePaso;
